import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useCelebration, CelebrationType } from '@/contexts/CelebrationContext';
import { ConfettiEffect } from './ConfettiEffect';
import { X } from 'lucide-react';

const typeStyles: Record<CelebrationType, {
  gradient: string;
  glow: string;
  confettiColors: string[];
}> = {
  achievement: {
    gradient: 'from-yellow-500/20 via-amber-500/10 to-orange-500/5',
    glow: 'shadow-[0_0_60px_rgba(251,191,36,0.3)]',
    confettiColors: ['#fbbf24', '#f59e0b', '#d97706', '#14b8a6', '#22d3ee']
  },
  levelUp: {
    gradient: 'from-purple-500/20 via-violet-500/10 to-indigo-500/5',
    glow: 'shadow-[0_0_60px_rgba(168,85,247,0.3)]',
    confettiColors: ['#a855f7', '#8b5cf6', '#7c3aed', '#ec4899', '#14b8a6']
  },
  streak: {
    gradient: 'from-orange-500/20 via-red-500/10 to-amber-500/5',
    glow: 'shadow-[0_0_60px_rgba(249,115,22,0.3)]',
    confettiColors: ['#f97316', '#fb923c', '#fdba74', '#fbbf24', '#ef4444']
  }
};

export function CelebrationToast() {
  const { currentCelebration, dismissCelebration } = useCelebration();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (currentCelebration) {
      setShowConfetti(true);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        dismissCelebration();
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowConfetti(false);
    }
  }, [currentCelebration, dismissCelebration]);

  return (
    <>
      <ConfettiEffect 
        isActive={showConfetti} 
        colors={currentCelebration ? typeStyles[currentCelebration.type].confettiColors : undefined}
        particleCount={150}
        duration={4000}
      />
      
      <AnimatePresence>
        {currentCelebration && (
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div 
              className="absolute inset-0 bg-background/80 backdrop-blur-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={dismissCelebration}
            />
            
            {/* Celebration Card */}
            <motion.div
              className={`relative w-full max-w-sm rounded-3xl bg-gradient-to-br ${typeStyles[currentCelebration.type].gradient} border border-border/50 ${typeStyles[currentCelebration.type].glow} overflow-hidden`}
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.6 }}
            >
              {/* Close button */}
              <button 
                onClick={dismissCelebration}
                className="absolute top-4 right-4 p-2 rounded-full bg-background/50 hover:bg-background/80 transition-colors z-10"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
              
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
              
              <div className="relative p-8 text-center">
                {/* Icon */}
                <motion.div
                  className="text-7xl mb-4"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.2, duration: 0.6 }}
                >
                  {currentCelebration.icon}
                </motion.div>
                
                {/* Title */}
                <motion.h2
                  className="text-2xl font-bold text-foreground mb-2"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {currentCelebration.title}
                </motion.h2>
                
                {/* Description */}
                <motion.p
                  className="text-muted-foreground mb-6"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {currentCelebration.description}
                </motion.p>
                
                {/* Continue button */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    onClick={dismissCelebration}
                    className="px-8 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-full"
                  >
                    Continue
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
